import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import FilterPanel from "../Common/FilterPanel";
import { AppContexts } from "../../../providers";

const CHART_COLORS = ["#14b8a6", "#0ea5e9", "#f59e0b", "#8b5cf6", "#ef4444"];

const axisProps = {
  x: {
    tick: { fontSize: 11, fill: "#94a3b8" },
    axisLine: { stroke: "#e2e8f0" },
    tickLine: false,
    interval: "preserveStartEnd",
  },
  y: {
    tick: { fontSize: 11, fill: "#94a3b8" },
    axisLine: false,
    tickLine: false,
    allowDecimals: false,
  },
  grid: {
    strokeDasharray: "3 3",
    stroke: "#f0f0f0",
    vertical: false,
  },
  tooltip: {
    contentStyle: {
      backgroundColor: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
      fontSize: "13px",
    },
  },
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatNumber = (value) => Number(value || 0).toLocaleString();

const normalizeMonthly = (monthly = []) =>
  monthly.map((item) => ({
    month: moment(item.month, "YYYY-MM").format("MMM YYYY"),
    responses: Number(item.responses || 0),
    averageScore: Number(item.average_score || 0),
    weightedScore: Number(item.weighted_average_score || 0),
  }));

const SummaryCard = ({ label, value, helper }) => (
  <div className="overview-metric-card">
    <div className="metric-header">
      <div className="metric-info">
        <h4 className="metric-title">{label}</h4>
        <div className="metric-total">{value}</div>
        {helper && <div className="metric-details"><span className="metric-avg">{helper}</span></div>}
      </div>
    </div>
  </div>
);

const EmptyChart = ({ text = "No data for this period" }) => (
  <div className="metric-no-data">{text}</div>
);

const SurveyReports = () => {
  const ReportsContext = useContext(AppContexts.ReportsContext);
  const SurveysContext = useContext(AppContexts.SurveysContext);
  const { loading, surveyReport, startDate, endDate, getSurveyReport } = ReportsContext;
  const { allSurveys, getSurveyList } = SurveysContext;

  const [selectedSurveyId, setSelectedSurveyId] = useState("");
  const isLoading = loading > 0;

  useEffect(() => {
    if (!allSurveys || allSurveys.length === 0) {
      getSurveyList();
    }
  }, []);

  useEffect(() => {
    if (!selectedSurveyId && allSurveys?.length > 0) {
      const firstSurveyWithResponses = allSurveys.find(
        (survey) => Number(survey.survey_results_count || 0) > 0,
      );
      setSelectedSurveyId(String((firstSurveyWithResponses || allSurveys[0]).survey_id));
    }
  }, [allSurveys, selectedSurveyId]);

  useEffect(() => {
    if (selectedSurveyId && startDate && endDate) {
      getSurveyReport(selectedSurveyId, startDate, endDate);
    }
  }, [selectedSurveyId, startDate, endDate]);

  const handleSubmit = useCallback(
    (start, end) => {
      if (selectedSurveyId && start && end) {
        getSurveyReport(selectedSurveyId, start, end);
      }
    },
    [selectedSurveyId, getSurveyReport],
  );

  const summary = surveyReport?.summary || {};
  const monthly = useMemo(
    () => normalizeMonthly(surveyReport?.monthly || []),
    [surveyReport?.monthly],
  );
  const questionMetrics = surveyReport?.question_metrics || [];
  const byUnit = surveyReport?.by_unit || [];
  const byTag = surveyReport?.by_tag || [];
  const hasReport = !!surveyReport?.survey_id;

  const selectedSurvey = useMemo(
    () => (allSurveys || []).find((survey) => String(survey.survey_id) === String(selectedSurveyId)),
    [allSurveys, selectedSurveyId],
  );

  const renderQuestionChart = (question, index) => {
    const data = (question.distribution || [])
      .map((item) => ({
        answer: String(item.answer || "No answer"),
        count: Number(item.count || 0),
      }))
      .slice(0, 12);

    if (!data.length) return <EmptyChart />;

    return (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 5, right: 12, left: -10, bottom: 16 }}>
          <CartesianGrid {...axisProps.grid} />
          <XAxis {...axisProps.x} dataKey="answer" angle={-20} textAnchor="end" height={54} />
          <YAxis {...axisProps.y} />
          <Tooltip {...axisProps.tooltip} />
          <Bar dataKey="count" fill={CHART_COLORS[index % CHART_COLORS.length]} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <li className="reports-tab-content">
      <h2>Survey Reports</h2>
      <div>
        <FilterPanel onSubmit={handleSubmit}>
          <div className="uk-form-controls uk-margin-small-left">
            <select
              className="uk-select"
              value={selectedSurveyId}
              onChange={(e) => setSelectedSurveyId(e.target.value)}
            >
              {(allSurveys || []).map((survey) => (
                <option key={survey.survey_id} value={survey.survey_id}>
                  {survey.name || `Survey ${survey.survey_id}`} ({Number(survey.survey_results_count || 0).toLocaleString()})
                </option>
              ))}
            </select>
          </div>
        </FilterPanel>

        {!selectedSurveyId && (
          <div className="overview-empty">
            <p>Select a survey to view reporting data.</p>
          </div>
        )}

        {selectedSurveyId && isLoading && !hasReport && (
          <div className="overview-metric-grid">
            {["Responses", "Average Score", "Weighted Average", "Handled"].map((label) => (
              <div key={label} className="overview-metric-card overview-skeleton">
                <div className="skeleton-line skeleton-metric-label" />
                <div className="skeleton-line skeleton-metric-number" />
                <div className="skeleton-line skeleton-metric-footer" />
              </div>
            ))}
          </div>
        )}

        {selectedSurveyId && hasReport && (
          <>
            <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-top uk-margin-small-bottom">
              <div className="uk-margin-small-right">
                <h3 className="uk-margin-remove">{surveyReport.name || selectedSurvey?.name || "Survey"}</h3>
                <div className="secondary">
                  {moment(startDate).format("MMM D, YYYY")} - {moment(endDate).format("MMM D, YYYY")}
                </div> 
              </div>
              <a className="uk-button hms-btn" href={`/surveys/${selectedSurveyId}/result`}>
                View Responses
              </a>
            </div>

            <div className="overview-metric-grid">
              <SummaryCard label="Responses" value={formatNumber(summary.responses)} helper="Submitted surveys" />
              <SummaryCard label="Average Score" value={formatPercent(summary.average_score)} helper="Range questions only" />
              <SummaryCard label="Weighted Average" value={formatPercent(summary.weighted_average_score)} helper="Default weight: 1 per scored question" />
              <SummaryCard label="Handled" value={`${formatNumber(summary.handled)} / ${formatNumber(summary.responses)}`} helper={`${formatNumber(summary.unhandled)} unhandled`} />
            </div>

            <div className="overview-wide-grid">
              <div className="overview-chart-card">
                <h4 className="chart-card-title">Monthly Tracker</h4>
                {monthly.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthly} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                      <CartesianGrid {...axisProps.grid} />
                      <XAxis {...axisProps.x} dataKey="month" />
                      <YAxis {...axisProps.y} yAxisId="left" />
                      <YAxis {...axisProps.y} yAxisId="right" orientation="right" domain={[0, 100]} />
                      <Tooltip {...axisProps.tooltip} />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="responses" name="Responses" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="weightedScore" name="Weighted Score" stroke="#14b8a6" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart />
                )}
              </div>

              <div className="overview-chart-card">
                <h4 className="chart-card-title">Monthly Score Trend</h4>
                {monthly.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthly} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                      <CartesianGrid {...axisProps.grid} />
                      <XAxis {...axisProps.x} dataKey="month" />
                      <YAxis {...axisProps.y} domain={[0, 100]} />
                      <Tooltip {...axisProps.tooltip} />
                      <Legend />
                      <Bar dataKey="averageScore" name="Average Score" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="weightedScore" name="Weighted Score" fill="#14b8a6" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart />
                )}
              </div>
            </div>

            <h3 className="uk-margin-small-top">Questions</h3>
            <div className="overview-wide-grid">
              {questionMetrics.map((question, index) => (
                <div className="overview-chart-card" key={question.survey_question_id}>
                  <div className="uk-flex uk-flex-between uk-flex-top uk-margin-small-bottom">
                    <h4 className="chart-card-title">{question.question}</h4>
                    <div>
                      <span>{formatNumber(question.responses)} responses</span>
                      {(question.question_type || "").startsWith("range-") && (
                        <strong>{formatPercent(question.average_score)}</strong>
                      )}
                    </div>
                  </div>
                  {renderQuestionChart(question, index)}
                </div>
              ))}
              {questionMetrics.length === 0 && <EmptyChart text="No question responses for this period" />}
            </div>

            <div className="overview-wide-grid">
              <div className="overview-chart-card">
                <h4 className="chart-card-title">By Unit</h4>
                <table className="uk-table uk-table-small uk-table-divider hms-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Responses</th>
                      <th>Weighted Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byUnit.map((unit) => (
                      <tr key={`${unit.rental_id}-${unit.pms_id}`}>
                        <td>{unit.rental_name || unit.pms_id || "Unknown Unit"}</td>
                        <td>{formatNumber(unit.responses)}</td>
                        <td>{formatPercent(unit.weighted_average_score)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {byUnit.length === 0 && <EmptyChart text="No unit breakdown available" />}
              </div>

              <div className="overview-chart-card">
                <h4 className="chart-card-title">By Tag</h4>
                <table className="uk-table uk-table-small uk-table-divider hms-table">
                  <thead>
                    <tr>
                      <th>Tag</th>
                      <th>Responses</th>
                      <th>Weighted Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTag.map((tag) => (
                      <tr key={tag.tag_id}>
                        <td>{tag.tag_name}</td>
                        <td>{formatNumber(tag.responses)}</td>
                        <td>{formatPercent(tag.weighted_average_score)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {byTag.length === 0 && <EmptyChart text="No tag breakdown available" />}
              </div>
            </div>
          </>
        )}
      </div>
    </li>
  );
};

export default SurveyReports;
